import React, { Component } from "react";

// CSS
import "../../styles/newDesign.scss";
import "./Courses.scss";

// Components
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";

// Helper
import { resolveTenant, getCourse, requestSubscription, isLoggedIn, saveCourseProgress, getItemProgress, cachedTenant } from "../../Helper/Api";
import VideoPlayer from "./VideoPlayer";
import Model3DViewer from "./Model3DViewer";
import { t } from "../../Helper/i18n";
import Toast from "../../components/Toast/Toast";

const properties = require("../../properties.json");

const ITEM_ICON = {
  VIDEO: "fa-play-circle",
  AUDIO: "fa-volume-up",
  IMAGE: "fa-image",
  DOCUMENT: "fa-file-alt",
  MODEL_3D: "fa-cube",
  ARTICLE: "fa-book-open",
};

class CourseDetail extends Component {
  audioRef = React.createRef();
  audioProgressTimer = null;

  state = {
    tenant: cachedTenant(),
    notFound: false,
    course: null,
    subscribing: false,
    msg: null,
    playingItem: null,
    resumeSeconds: null,
  };

  componentDidMount() {
    const id = this.props.match.params.id;
    resolveTenant()
      .then((tenant) => {
        this.setState({ tenant });
        return getCourse(id);
      })
      .then((course) => {
        document.title = properties.site_name + " - " + course.title;
        this.setState({ course });
        // Resume where the student left off (see CourseResponse.lastItemId/lastPositionSeconds) --
        // find the item across every section rather than opening it via openItem, since that would
        // reset the tracked position back to the start.
        // a link from the library / calendar / recipes opens that item straight away (?item=ID)
        const wanted = Number(new URLSearchParams(window.location.search).get("item"));
        const linked = wanted ? (course.sections || []).flatMap((s) => s.items || []).find((i) => i.id === wanted) : null;
        if (linked && !linked.locked && (linked.articleText || linked.contentUrl || linked.hlsManifestUrl)) {
          this.openItem(linked);
        } else if (course.lastItemId) {
          const lastItem = (course.sections || [])
            .flatMap((s) => s.items || [])
            .find((i) => i.id === course.lastItemId);
          if (lastItem && !lastItem.locked && (lastItem.contentUrl || lastItem.hlsManifestUrl)) {
            this.setState({ playingItem: lastItem, resumeSeconds: course.lastPositionSeconds || null });
          }
        }
      })
      .catch(() => this.setState({ notFound: true }));
  }

  componentWillUnmount() {
    this.stopAudioProgressReporting();
  }

  reportProgress = (itemId, positionSeconds) => {
    const { course } = this.state;
    if (course) saveCourseProgress(course.id, itemId, positionSeconds);
  };

  stopAudioProgressReporting = () => {
    if (this.audioProgressTimer) {
      clearInterval(this.audioProgressTimer);
      this.audioProgressTimer = null;
    }
    const audio = this.audioRef.current;
    if (audio && this.state.playingItem) {
      this.reportProgress(this.state.playingItem.id, Math.floor(audio.currentTime));
    }
  };

  startAudioProgressReporting = (itemId) => {
    this.audioProgressTimer = setInterval(() => {
      const audio = this.audioRef.current;
      if (audio && !audio.paused && !audio.seeking) {
        this.reportProgress(itemId, Math.floor(audio.currentTime));
      }
    }, 10000);
  };

  subscribe = () => {
    if (!isLoggedIn()) {
      window.location.href = "/sign-in";
      return;
    }
    const { course, tenant } = this.state;
    this.setState({ subscribing: true, msg: null });
    requestSubscription({ periodId: course.periodId, branchId: tenant.branchId })
      .then(() => {
        this.setState({ subscribing: false, msg: { text: t("subSent"), type: "success" } });
      })
      .catch((error) => {
        this.setState({ subscribing: false, msg: { text: error.message || t("subFailed"), type: "warning" } });
      });
  };

  openItem = (item) => {
    if (!item.locked && !(item.contentUrl || item.hlsManifestUrl) && item.processingStatus && item.processingStatus !== "READY") {
      this.setState({ msg: { text: item.processingStatus === "FAILED" ? t("videoFailed") : t("videoPreparingMsg"), type: "warning" } });
      return;
    }
    if (item.locked || !(item.articleText || item.contentUrl || item.hlsManifestUrl)) return;
    this.stopAudioProgressReporting();
    if (item.itemType === "ARTICLE") {
      // a written page: nothing to play, so it opens at once and just counts as opened
      this.setState({ playingItem: item, resumeSeconds: null });
      this.reportProgress(item.id, null);
      return;
    }
    if (item.itemType === "DOCUMENT") {
      window.open(item.contentUrl, "_blank", "noreferrer");
      this.reportProgress(item.id, null);
      return;
    }
    // A fresh click always starts from the top -- resuming mid-item only happens via the
    // auto-selected lastItemId path in componentDidMount, never from clicking it again.
    const start = (resume) => {
      this.setState({ playingItem: item, resumeSeconds: resume });
      this.reportProgress(item.id, item.itemType === "VIDEO" || item.itemType === "AUDIO" ? 0 : null);
    };
    // a lesson the student already started resumes where they stopped, not from the top
    if (item.itemType === "VIDEO" && isLoggedIn() && this.state.course) {
      getItemProgress(this.state.course.id, item.id).then((seconds) => start(seconds > 0 ? seconds : null));
    } else {
      start(null);
    }
  };

  renderFrame() {
    const { playingItem, course, resumeSeconds } = this.state;

    if (!playingItem) {
      return (
        <div className="player-frame-placeholder">
          {course.image ? <img src={course.image} alt={course.title} /> : <i className="fas fa-book-open"></i>}
          <p>{t("pickItem")}</p>
        </div>
      );
    }
    if (playingItem.itemType === "VIDEO") {
      return (
        <VideoPlayer
          key={playingItem.id}
          itemId={playingItem.id}
          src={playingItem.contentUrl}
          hlsManifestUrl={playingItem.hlsManifestUrl}
          viewerLabel={course.viewerLabel}
          resumeSeconds={resumeSeconds}
          onProgress={(seconds) => this.reportProgress(playingItem.id, seconds)}
        />
      );
    }
    if (playingItem.itemType === "AUDIO") {
      return (
        <div className="audio-player-frame">
          <i className="fas fa-volume-up"></i>
          <p>{playingItem.title}</p>
          <audio
            key={playingItem.id}
            ref={this.audioRef}
            src={playingItem.contentUrl}
            controls
            controlsList="nodownload noremoteplayback"
            disableRemotePlayback
            onContextMenu={(e) => e.preventDefault()}
            autoPlay
            style={{ width: "100%" }}
            onLoadedMetadata={(e) => {
              if (resumeSeconds) e.target.currentTime = resumeSeconds;
              this.startAudioProgressReporting(playingItem.id);
            }}
          />
        </div>
      );
    }
    if (playingItem.itemType === "IMAGE") {
      return (
        <img
          className="image-player-frame"
          src={playingItem.contentUrl}
          alt={playingItem.title}
          draggable={false}
          onContextMenu={(e) => e.preventDefault()}
        />
      );
    }
    if (playingItem.itemType === "MODEL_3D") {
      return <Model3DViewer key={playingItem.id} src={playingItem.contentUrl} />;
    }
    if (playingItem.itemType === "ARTICLE") {
      // the text is shown as text (line breaks kept), never as HTML
      return (
        <article className="article-frame" key={playingItem.id}>
          {playingItem.thumb && <img className="article-img" src={playingItem.thumb} alt={playingItem.title} draggable={false} />}
          <h2>{playingItem.title}</h2>
          {playingItem.description && <p className="article-lead">{playingItem.description}</p>}
          {playingItem.contentUrl && <video className="article-video" src={playingItem.contentUrl} controls controlsList="nodownload" />}
          <div className="article-text">{playingItem.articleText}</div>
        </article>
      );
    }
    return null;
  }

  render() {
    const { tenant, notFound, course, subscribing, msg } = this.state;

    if (notFound) {
      return (
        <div className="page">
          <div className="container text-center">
            <p>{t("notFound")}</p>
          </div>
        </div>
      );
    }

    if (!tenant || !course) return null;

    return (
      <>
        <Header tenant={tenant} />

        <div className="nd-page">
          <div className="nd-hero">
            <div className="nd-wrap">
              <div className="nd-eyebrow">{t("course")}</div>
              <h1 className="nd-title">{course.title}</h1>
            </div>
          </div>

          <div className="nd-section nd-wrap">
            <div className="course-detail-page">
              <div>
                <div className="player-frame">{this.renderFrame()}</div>

                {course.description && <p className="course-full-desc">{course.description}</p>}

                {(course.sections || []).map((section) => (
                  <div className="course-section" key={section.id}>
                    <h4>{section.title}</h4>
                    <div className="course-items">
                      {(section.items || []).map((item) => (
                        <div
                          className={`course-item ${item.locked ? "locked" : "playable"} ${this.state.playingItem && this.state.playingItem.id === item.id ? "active" : ""}`}
                          key={item.id}
                          onClick={() => this.openItem(item)}
                        >
                          <i className={`fas ${item.locked ? "fa-lock" : ITEM_ICON[item.itemType] || "fa-play-circle"}`}></i>
                          <span>{item.title}</span>
                          {item.isFreePreview && !item.locked && <span className="preview-tag">{t("freePreview")}</span>}
                          {!item.locked && !item.contentUrl && !item.hlsManifestUrl && item.processingStatus && item.processingStatus !== "READY" && (
                            <span className="preview-tag">{item.processingStatus === "FAILED" ? t("videoFailed") : t("videoPreparing")}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div>
                <div className="subscribe-box">
                  {course.image && <img src={course.image} alt={course.title} />}
                  <Toast msg={msg} />
                  {course.enrolled ? (
                    <p className="already-enrolled">{t("enrolled")}</p>
                  ) : (
                    <button type="button" className={`crs-enroll ${subscribing ? "loading" : ""}`} disabled={subscribing} onClick={this.subscribe}>
                      {subscribing ? t("sending") : t("requestSub")}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <Footer tenant={tenant} />
      </>
    );
  }
}

export default CourseDetail;
